/** El tablero: una columna por opción de la propiedad que agrupa, y las filas se arrastran
 * entre columnas, que es lo mismo que cambiarles ese valor. */
import { useState } from 'react'

import { IconoDePagina } from '../Arbol'
import type { AccionesDeVista } from './BaseVista'
import { Pastilla, Persona } from './Celdas'
import { useEspacio } from '../contexto'
import { fechaCorta, opcionDe } from './logica'
import type { FechaValor, Fila, Opcion, Propiedad } from './tipos'

// Qué fila se arrastra: de módulo, porque el navegador no deja leer los datos hasta soltar.
let arrastrando: number | null = null

export function Tablero(p: AccionesDeVista & { todas: Fila[] }) {
  const { esquema, vista, filas, editable } = p
  const grupo = esquema.properties.find((c) => c.id === vista.group_by)
  const [sobre, setSobre] = useState<string | null>(null)

  if (!grupo || !(grupo.type === 'select' || grupo.type === 'status')) {
    return (
      <div className="bd__vacio">
        Este tablero necesita una propiedad de tipo Selección o Estado para formar columnas.
        {editable && (
          <select aria-label="Agrupar por" value="" onChange={(e) => p.alCambiarVista({ ...vista, group_by: e.target.value })}>
            <option value="">Elige una propiedad…</option>
            {esquema.properties.filter((c) => c.type === 'select' || c.type === 'status').map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
      </div>
    )
  }

  const columnas: { id: string | null; opcion: Opcion | null }[] = [
    ...(grupo.options ?? []).map((o) => ({ id: o.id, opcion: o })),
    { id: null, opcion: null },
  ]
  const ocultas = new Set(vista.hidden ?? [])
  const visibles = esquema.properties.filter((c) => c.id !== grupo.id && !ocultas.has(c.id))

  const soltar = (destino: string | null, antesDe?: Fila) => {
    const id = arrastrando
    arrastrando = null
    setSobre(null)
    if (id == null) return
    const fila = p.todas.find((f) => f.id === id)
    if (!fila) return

    const hermanas = filas.filter((f) => (f.props[grupo.id] ?? null) === destino && f.id !== id)
    let posicion: number
    if (antesDe) {
      const i = hermanas.findIndex((f) => f.id === antesDe.id)
      const anterior = hermanas[i - 1]
      posicion = anterior ? (anterior.position + antesDe.position) / 2 : antesDe.position - 1024
    } else {
      posicion = (hermanas[hermanas.length - 1]?.position ?? 0) + 1024
    }
    p.alCambiarFila(id, { props: { [grupo.id]: destino }, position: posicion })
  }

  return (
    <div className="tablero-bd">
      {columnas.map((col) => {
        const clave = col.id ?? '__ninguno'
        const dentro = filas.filter((f) => (f.props[grupo.id] ?? null) === col.id)
        if (col.id === null && dentro.length === 0 && !editable) return null
        return (
          <section
            key={clave}
            className={`columna-bd${sobre === clave ? ' columna-bd--sobre' : ''}`}
            onDragOver={(e) => { if (arrastrando != null && editable) { e.preventDefault(); setSobre(clave) } }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setSobre(null) }}
            onDrop={(e) => { e.preventDefault(); soltar(col.id) }}
          >
            <header className="columna-bd__cabecera">
              {col.opcion ? <Pastilla opcion={col.opcion} /> : <span className="columna-bd__ninguno">Sin {grupo.name.toLowerCase()}</span>}
              <span className="columna-bd__cuenta">{dentro.length}</span>
            </header>
            <div className="columna-bd__lista">
              {dentro.map((f) => (
                <Tarjeta key={f.id} fila={f} visibles={visibles} editable={editable}
                         alAbrir={() => p.alAbrir(f.id)}
                         alSoltarSobre={() => soltar(col.id, f)} />
              ))}
            </div>
            {editable && (
              <button className="columna-bd__nueva" onClick={() => p.alNuevaFila(col.id ? { [grupo.id]: col.id } : {})}>
                ＋ Nueva
              </button>
            )}
          </section>
        )
      })}
    </div>
  )
}

export function ValoresEnTarjeta({ fila, visibles }: { fila: Fila; visibles: Propiedad[] }) {
  const { personas } = useEspacio()
  const trozos = visibles.flatMap((c) => {
    const v = fila.props[c.id]
    if (v == null || v === '' || v === false) return []
    if (c.type === 'select' || c.type === 'status') {
      const o = opcionDe(c, v)
      return o ? [<Pastilla key={c.id} opcion={o} />] : []
    }
    if (c.type === 'multi_select' && Array.isArray(v)) {
      return v.map((id) => opcionDe(c, id)).filter((o): o is Opcion => !!o)
        .map((o) => <Pastilla key={c.id + o.id} opcion={o} />)
    }
    if (c.type === 'person' && Array.isArray(v)) {
      return v.map((id) => <Persona key={c.id + id} nombre={personas.find((p) => p.id === id)?.nombre ?? '?'} />)
    }
    if (c.type === 'date') return [<span key={c.id} className="tarjeta-bd__dato">▦ {fechaCorta((v as FechaValor).start)}</span>]
    if (c.type === 'checkbox') return [<span key={c.id} className="tarjeta-bd__dato">☑ {c.name}</span>]
    return [<span key={c.id} className="tarjeta-bd__dato">{String(v)}</span>]
  })
  return trozos.length ? <div className="tarjeta-bd__valores">{trozos}</div> : null
}

function Tarjeta({ fila, visibles, editable, alAbrir, alSoltarSobre }: {
  fila: Fila; visibles: Propiedad[]; editable: boolean; alAbrir: () => void; alSoltarSobre: () => void
}) {
  const [encima, setEncima] = useState(false)
  return (
    <article
      className={`tarjeta-bd${encima ? ' tarjeta-bd--encima' : ''}`}
      draggable={editable}
      onDragStart={(e) => { arrastrando = fila.id; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(fila.id)) }}
      onDragEnd={() => { arrastrando = null }}
      onDragOver={(e) => { if (arrastrando != null && arrastrando !== fila.id) { e.preventDefault(); e.stopPropagation(); setEncima(true) } }}
      onDragLeave={() => setEncima(false)}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setEncima(false); alSoltarSobre() }}
      onClick={alAbrir}
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && alAbrir()}
    >
      <div className="tarjeta-bd__titulo">
        <IconoDePagina icono={fila.icon} tam={16} /> {fila.title || 'Sin título'}
      </div>
      <ValoresEnTarjeta fila={fila} visibles={visibles} />
    </article>
  )
}
